export const GEOGRAPHIC_TOUR_ID = 'geographic';
export const GEOGRAPHIC_TOUR_STORAGE_KEY = 'cityExplorer:geographicMapTour:v1';
export const GEOGRAPHIC_DATA_SOURCES_STEP_INDEX = 4;

const GEOGRAPHIC_CATEGORY_IDS = new Set([
  'statewide',
  'planning_regions',
  'target_cities',
  'school_districts',
]);

const FLY_TO_CATEGORY_IDS = new Set(['planning_regions', 'target_cities']);

export const isGeographicCategoryId = (id) => GEOGRAPHIC_CATEGORY_IDS.has(String(id || ''));

export const isGeographicCategory = (category) =>
  category?.tour === GEOGRAPHIC_TOUR_ID || isGeographicCategoryId(category?.id);

export const locationHasGeographicMaps = (categories = []) =>
  categories.some((category) => isGeographicCategory(category));

export const isGeographicMapItem = (item) =>
  item?.type === 'map' && isGeographicCategory(item.category);

const DATA_SOURCES_STEP = {
  id: 'data-sources',
  title: 'Data sources',
  body: 'The spreadsheets behind this map are in the left panel under Data Sets.',
  cardAlign: 'bottom-left',
};

const BASE_STEPS = [
  {
    id: 'shading',
    title: 'Polygon shading',
    body: 'Areas are colored by projected K–12 population change from 2026 to 2031. Red means decline of 5% or more, white is roughly stable, and green means growth of 5% or more. The scale is in the legend below the map.',
    spotlight: 'legend-box',
  },
  {
    id: 'hover-zoom',
    title: 'Hover and zoom',
    body: 'Hover a polygon for population, trend, race, income, and how many charters sit in that area. Use the + and − buttons in the top left to zoom, or scroll or pinch.',
    spotlight: 'map-zoom',
    spotlightLabel: 'Zoom',
  },
  {
    id: 'school-popups',
    title: 'Charter details',
    body: 'Click a charter pin for name, address, Senate and House district badges, 2025–26 enrollment, projected enrollment trend, and grades. The highlighted card is an example of what you will see.',
    spotlight: 'example-popup',
  },
  {
    id: 'school-markers',
    title: 'Charter pins',
    body: 'Circles are charter schools, colored by projected enrollment trend from 2025–26 to 2030–31 — green Growing, gray Stable, red Declining, light gray N/A. Pin color is not the same as the polygon fill.',
    spotlight: 'map-schools-east',
    spotlightLabel: 'Charter pins',
  },
];

const FLY_TO_HOVER_STEP = {
  ...BASE_STEPS[1],
  body: 'Hover a polygon for population, trend, race, income, and how many charters sit in that area. Use the + and − buttons in the top left to zoom, or scroll or pinch. Click a name in the left legend to fly there.',
};

/**
 * Region and city maps have a tall clickable legend; state and district maps
 * have a compact color-scale box. Same walkthrough either way, with the
 * shading spotlight swapped so it actually covers the legend.
 */
export const geographicTourSteps = (category, { includeDataSources = true } = {}) => {
  const flyTo = FLY_TO_CATEGORY_IDS.has(category?.id);
  const steps = BASE_STEPS.map((step) => {
    if (step.id === 'shading') {
      return { ...step, spotlight: flyTo ? 'legend-strip' : 'legend-box' };
    }
    if (step.id === 'hover-zoom' && flyTo) return FLY_TO_HOVER_STEP;
    return step;
  });
  if (includeDataSources) steps.push(DATA_SOURCES_STEP);
  return steps;
};

const readFlag = (key) => {
  try {
    return window.localStorage.getItem(key) === '1';
  } catch (_err) {
    return false;
  }
};

const writeFlag = (key) => {
  try {
    window.localStorage.setItem(key, '1');
  } catch (_err) {
    // Private browsing or disabled storage should not break the map.
  }
};

export const readGeographicTourDismissed = () => readFlag(GEOGRAPHIC_TOUR_STORAGE_KEY);
export const writeGeographicTourDismissed = () => writeFlag(GEOGRAPHIC_TOUR_STORAGE_KEY);

export const shouldShowGeographicTour = ({
  hasGeographicCategory,
  viewingGeographicMap,
  mapLoaded,
  isFullscreen,
  dismissed,
  replaying,
}) =>
  Boolean(
    hasGeographicCategory &&
      viewingGeographicMap &&
      mapLoaded &&
      !isFullscreen &&
      (!dismissed || replaying)
  );
